import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react';
import { useAuth } from './AuthContext';
import {
  Subscription,
  checkSubscriptionStatus,
} from '../services/subscriptionService';

interface SubscriptionContextValue {
  subscription: Subscription | null;
  isActive: boolean;
  daysRemaining: number;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextValue | undefined>(
  undefined
);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, loading: authLoading } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [isActive, setIsActive] = useState(false);
  const [daysRemaining, setDaysRemaining] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadedUserId, setLoadedUserId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const requestId = useRef(0);
  const verifiedUserId = useRef<string | null>(null);

  const refresh = useCallback(async () => {
    const currentRequest = ++requestId.current;
    if (!user?.uid) {
      verifiedUserId.current = null;
      setSubscription(null);
      setIsActive(false);
      setDaysRemaining(0);
      setLoading(false);
      setLoadedUserId(null);
      setError(null);
      return;
    }

    try {
      // Polling and window focus must not unmount a verified user's editor.
      // A new account still requires a full initial verification.
      const needsInitialVerification = verifiedUserId.current !== user.uid;
      setLoading(needsInitialVerification);
      if (needsInitialVerification) {
        setSubscription(null);
        setIsActive(false);
        setDaysRemaining(0);
      }
      setError(null);
      const sub = await checkSubscriptionStatus(user.uid);
      if (currentRequest !== requestId.current) return;
      verifiedUserId.current = user.uid;
      setSubscription(sub);

      if (sub) {
        const cancelledAccess = sub.status === 'cancelled' && !!sub.accessEndsAt && Date.now() <= new Date(sub.accessEndsAt).getTime();
        setIsActive(sub.status === 'active' || sub.status === 'trial' || cancelledAccess);

        if (sub.status === 'trial') {
          const days = Math.ceil((new Date(sub.trialEndDate).getTime() - Date.now()) / 86400000);
          setDaysRemaining(Number.isFinite(days) ? Math.max(0, days) : 0);
        } else {
          setDaysRemaining(0);
        }
      } else {
        setIsActive(false);
        setDaysRemaining(0);
      }
    } catch (error) {
      if (currentRequest !== requestId.current) return;
      console.error('Erro ao atualizar assinatura:', error);
      setError('Não foi possível verificar sua assinatura. Tente novamente.');
    } finally {
      if (currentRequest === requestId.current) {
        setLoadedUserId(user.uid);
        setLoading(false);
      }
    }
  }, [user?.uid]);

  useEffect(() => {
    refresh();

    // Reflect administrative grants and revocations while the application is open.
    const interval = setInterval(() => {
      refresh();
    }, 30 * 1000);

    const onFocus = () => { void refresh(); };
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      clearInterval(interval);
      requestId.current++;
    };
  }, [refresh]);

  return (
    <SubscriptionContext.Provider
      value={{
        subscription,
        isActive,
        daysRemaining,
        loading: authLoading || loading || (!!user && loadedUserId !== user.uid),
        error,
        refresh,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export function useSubscription() {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return ctx;
}

export function useTrialStatus() {
  const { subscription, daysRemaining, isActive } = useSubscription();
  const isTrial = subscription?.status === 'trial';
  const isExpired = subscription?.status === 'expired';
  const isNearExpiration = isTrial && daysRemaining <= 2;

  return {
    isTrial,
    isExpired,
    isNearExpiration,
    daysRemaining,
    isActive,
  };
}

